import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Establishments from "./pages/Establishments";
import Subscriptions from "./pages/Subscriptions";
import Invoices from "./pages/Invoices";
import Payments from "./pages/Payments";
import Receipts from "./pages/Receipts";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import UsersManagement from "./pages/UsersManagement";
import ChangePassword from "./pages/ChangePassword";
import { ShieldCheck } from "lucide-react";

function FullLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-slate-950">
      <div className="flex items-center gap-3 text-white">
        <ShieldCheck className="h-6 w-6 text-teal-400 animate-pulse" />
        <span className="font-display font-bold">Scolaris Central</span>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return <Layout>{children}</Layout>;
}

const CENTRAL = ["principal_admin", "finance_agent"];

function CentralOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (!CENTRAL.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function PrincipalOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (user.role !== "principal_admin") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/clients" element={<CentralOnly><Customers /></CentralOnly>} />
      <Route path="/clients/:id" element={<Protected><CustomerDetail /></Protected>} />
      <Route path="/etablissements" element={<Protected><Establishments /></Protected>} />
      <Route path="/abonnements" element={<Protected><Subscriptions /></Protected>} />
      <Route path="/factures" element={<Protected><Invoices /></Protected>} />
      <Route path="/paiements" element={<Protected><Payments /></Protected>} />
      <Route path="/recus" element={<Protected><Receipts /></Protected>} />
      <Route path="/rapports" element={<CentralOnly><Reports /></CentralOnly>} />
      <Route path="/utilisateurs" element={<PrincipalOnly><UsersManagement /></PrincipalOnly>} />
      <Route path="/parametres" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
